import type { GeocodeHit } from "../types";

const PHOTON = "https://photon.komoot.io";

interface PhotonFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    name?: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    county?: string;
  };
}

function labelOf(feature: PhotonFeature): string {
  const p = feature.properties ?? {};
  return [p.name, p.city, p.state, p.country].filter(Boolean).join(", ");
}

function toHit(feature: PhotonFeature): GeocodeHit | null {
  const coords = feature.geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const [lon, lat] = coords;
  const name = labelOf(feature);
  if (!name) return null;
  return { name, latitude: lat, longitude: lon };
}

export async function searchPlaces(query: string): Promise<GeocodeHit[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const url = new URL(`${PHOTON}/api/`);
  url.searchParams.set("q", q);
  url.searchParams.set("limit", "6");
  url.searchParams.set("lang", "en");
  url.searchParams.set("bbox", "-170,24,-52,72");
  const res = await fetch(url);
  if (!res.ok) throw new Error("Search failed");
  const json = (await res.json()) as { features?: PhotonFeature[] };
  return (json.features ?? []).map(toHit).filter((h): h is GeocodeHit => h !== null);
}

export async function reversePlace(
  lat: number,
  lon: number,
): Promise<string | null> {
  const url = new URL(`${PHOTON}/reverse`);
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lon));
  const res = await fetch(url);
  if (!res.ok) return null;
  const json = (await res.json()) as { features?: PhotonFeature[] };
  const hit = json.features?.[0] ? toHit(json.features[0]) : null;
  return hit?.name ?? null;
}
